import React, { useState } from "react";
import {
  forgotPasswordStep1Api,
  forgotPasswordStep2Api,
  changePasswordApi,
} from "../../services/authApi";
import {
  checkResponseData,
  checkErrorMessage,
} from "../../utils/responseHandler";
import { useToast, TOAST_TYPES } from "../../utils/toast";
import "./ForgotPasswordForm.css";

const ForgotPasswordForm = ({ onSwitchToLogin }) => {
  const [step, setStep] = useState(1);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [confirmCode, setConfirmCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState(null);
  const [refreshToken, setRefreshToken] = useState(null);
  const [countdown, setCountdown] = useState(0);

  const { showToast, ToastContainer } = useToast();

  const startCountdown = () => {
    setCountdown(60);
    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSendCode = async () => {
    if (!phoneNumber.trim()) {
      showToast("لطفا شماره تلفن را وارد کنید", TOAST_TYPES.WARNING);
      return;
    }

    setLoading(true);
    try {
      const response = await forgotPasswordStep1Api(phoneNumber);
      const result = checkResponseData(response);

      if (result.success) {
        showToast(result.message || "کد تایید ارسال شد", TOAST_TYPES.SUCCESS);
        startCountdown();
        setStep(2);
      } else {
        showToast(result.message || "خطا در ارسال کد", TOAST_TYPES.ERROR);
      }
    } catch (error) {
      const errorDetails = checkErrorMessage(error);
      showToast(errorDetails.message, TOAST_TYPES.ERROR);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!confirmCode.trim()) {
      showToast("لطفا کد تایید را وارد کنید", TOAST_TYPES.WARNING);
      return;
    }

    setLoading(true);
    try {
      const response = await forgotPasswordStep2Api(phoneNumber, confirmCode);
      const result = checkResponseData(response);

      if (result.success) {
        // ذخیره توکن‌ها در localStorage
        if (result.token) {
          localStorage.setItem("token", result.token);
          setToken(result.token);
        }
        if (result.refreshToken) {
          localStorage.setItem("refreshToken", result.refreshToken);
          setRefreshToken(result.refreshToken);
        }

        showToast(result.message || "تایید شد", TOAST_TYPES.SUCCESS);
        setStep(3);
      } else {
        showToast(result.message || "کد نامعتبر است", TOAST_TYPES.ERROR);
      }
    } catch (error) {
      const errorDetails = checkErrorMessage(error);
      showToast(errorDetails.message, TOAST_TYPES.ERROR);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (!newPassword.trim() || !confirmNewPassword.trim()) {
      showToast("لطفا رمز عبور جدید را وارد کنید", TOAST_TYPES.WARNING);
      return;
    }

    if (newPassword !== confirmNewPassword) {
      showToast("رمز عبور و تکرار آن مطابقت ندارند", TOAST_TYPES.ERROR);
      return;
    }

    if (newPassword.length < 6) {
      showToast("رمز عبور باید حداقل ۶ کاراکتر باشد", TOAST_TYPES.WARNING);
      return;
    }

    setLoading(true);
    try {
      // از token استفاده کن که قبلاً ذخیره کردیم
      const response = await changePasswordApi(
        newPassword,
        confirmNewPassword,
        token || localStorage.getItem("token")
      );
      const result = checkResponseData(response);

      if (result.success) {
        // به‌روزرسانی توکن‌ها اگر دوباره برگشتن
        if (result.token) {
          localStorage.setItem("token", result.token);
        }
        if (result.refreshToken) {
          localStorage.setItem("refreshToken", result.refreshToken);
        }

        showToast(
          result.message || "رمز عبور با موفقیت تغییر کرد",
          TOAST_TYPES.SUCCESS
        );

        // بعد از ۲ ثانیه رفرش کن تا وارد پلیر بشه
        setTimeout(() => window.location.reload(), 2000);
      } else {
        showToast(result.message || "خطا در تغییر رمز عبور", TOAST_TYPES.ERROR);
      }
    } catch (error) {
      const errorDetails = checkErrorMessage(error);
      showToast(errorDetails.message, TOAST_TYPES.ERROR);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <ToastContainer />
      <div className="forgot-password-form">
        {/* مرحله 1: شماره تلفن */}
        {step === 1 && (
          <div className="step-content">
            <p className="helper-text">
              شماره تلفن همراه خود را وارد کنید. کد تایید برای شما ارسال خواهد
              شد.
            </p>

            <input
              type="tel"
              className="auth-input"
              placeholder="شماره تلفن همراه"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              dir="ltr"
            />

            <button
              onClick={handleSendCode}
              disabled={loading}
              className="auth-button"
            >
              {loading ? (
                <span className="button-content">
                  <span className="loading-spinner"></span>
                  در حال ارسال...
                </span>
              ) : (
                "ارسال کد تایید"
              )}
            </button>
          </div>
        )}

        {/* مرحله 2: کد تایید */}
        {step === 2 && (
          <div className="step-content">
            <p className="helper-text">
              کد تایید به شماره {phoneNumber} ارسال شد
            </p>

            <input
              type="text"
              className="auth-input"
              placeholder="کد تایید ۶ رقمی"
              value={confirmCode}
              onChange={(e) => setConfirmCode(e.target.value)}
              maxLength="6"
              dir="ltr"
            />

            <button
              onClick={handleVerifyCode}
              disabled={loading}
              className="auth-button"
            >
              {loading ? (
                <span className="button-content">
                  <span className="loading-spinner"></span>
                  در حال بررسی...
                </span>
              ) : (
                "تایید کد"
              )}
            </button>

            {countdown > 0 ? (
              <p className="countdown">ارسال مجدد تا {countdown} ثانیه دیگر</p>
            ) : (
              <button
                onClick={handleSendCode}
                className="auth-link"
                style={{ marginTop: "1rem" }}
              >
                ارسال مجدد کد
              </button>
            )}
          </div>
        )}

        {/* مرحله 3: رمز عبور جدید */}
        {step === 3 && (
          <div className="step-content">
            <div className="password-input-container">
              <input
                type={showPassword ? "text" : "password"}
                className="auth-input password-input"
                placeholder="رمز عبور جدید (حداقل ۶ کاراکتر)"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? "👁️" : "👁️‍🗨️"}
              </button>
            </div>

            <div className="password-input-container">
              <input
                type={showConfirmPassword ? "text" : "password"}
                className="auth-input password-input"
                placeholder="تکرار رمز عبور جدید"
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? "👁️" : "👁️‍🗨️"}
              </button>
            </div>

            <button
              onClick={handleChangePassword}
              disabled={loading}
              className="auth-button"
            >
              {loading ? (
                <span className="button-content">
                  <span className="loading-spinner"></span>
                  در حال ثبت...
                </span>
              ) : (
                "تغییر رمز عبور"
              )}
            </button>
          </div>
        )}

        <div className="text-center mt-4">
          <button
            onClick={() => window.location.reload()} // رفرش برای رفتن به پلیر
            className="auth-link"
          >
            بازگشت به ویدئو پلیر
          </button>
        </div>
      </div>
    </>
  );
};

export default ForgotPasswordForm;
